import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { API_URL } from "../../Constants";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, Plus, Info, X } from "lucide-react";
import { useChatbotPage } from "../Chatbot/integration/PageContext";
import { SMS_FIELDS, SMS_APIS, DEFAULT_QUERY_MAP, mapSmsSpec, toObject, toRows } from "./smsChatbotAdapter";

const SmsConfigManager = () => {
  const { user } = useContext(AuthContext);
  const companyId = user?.accountType === "COMPANY" ? user?.id : user?.companyId;

  // --- Core States ---
  const [existing, setExisting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // --- Form States ---
  const [providerName, setProviderName] = useState("");
  const [httpMethod, setHttpMethod] = useState("GET");
  const [url, setUrl] = useState("");
  
  // GET Specific States
  const [queryParamMapping, setQueryParamMapping] = useState({
    phone: "phoneNumber",
    message: "message",
    senderId: "senderId",
    dltId: "templateID",
  });
  const [staticQueryParams, setStaticQueryParams] = useState([{ key: "", value: "" }]);

  // POST Specific States
  const [contentType, setContentType] = useState("application/json");
  const [customHeaders, setCustomHeaders] = useState([{ key: "Authorization", value: "" }]);
  const [payloadTemplate, setPayloadTemplate] = useState("");
  const [responseSuccessField, setResponseSuccessField] = useState("status");
  const [responseSuccessValue, setResponseSuccessValue] = useState("success");

  useEffect(() => {
    if (companyId) fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/smsConfig/company/${companyId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      
      if (res.data?.url) {
        const data = res.data;
        setExisting(true);
        setProviderName(data.providerName || "");
        setHttpMethod(data.httpMethod || "GET");
        setUrl(data.url || "");

        if (data.httpMethod === "GET") {
          setQueryParamMapping(data.queryParamMapping || queryParamMapping);
          if (data.staticQueryParams) {
            setStaticQueryParams(Object.entries(data.staticQueryParams).map(([k, v]) => ({ key: k, value: v })));
          }
        } else {
          setContentType(data.contentType || "application/json");
          setPayloadTemplate(data.payloadTemplate || "");
          setResponseSuccessField(data.responseSuccessField || "");
          setResponseSuccessValue(data.responseSuccessValue || "");
          if (data.customHeaders) {
            setCustomHeaders(Object.entries(data.customHeaders).map(([k, v]) => ({ key: k, value: v })));
          }
        }
      } else {
        setExisting(false);
      }
    } catch (err) {
      setExisting(false);
    } finally {
      setLoading(false);
    }
  };

  // --- Helper to convert array of {key, value} to Object ---
  const arrayToObject = (arr) => {
    return arr.reduce((acc, curr) => {
      if (curr.key.trim()) acc[curr.key.trim()] = curr.value.trim();
      return acc;
    }, {});
  };

  const handleSaveOrUpdate = async () => {
    if (!providerName || !url) {
      toast.error("Provider Name and URL are required.");
      return false;
    }

    let payload = {
      companyId,
      providerName,
      httpMethod,
      url,
    };

    if (httpMethod === "GET") {
      payload = {
        ...payload,
        queryParamMapping,
        staticQueryParams: arrayToObject(staticQueryParams),
      };
    } else {
      payload = {
        ...payload,
        contentType,
        customHeaders: arrayToObject(customHeaders),
        payloadTemplate,
        responseSuccessField,
        responseSuccessValue,
      };
    }

    setLoading(true);
    try {
      const endpoint = existing ? `${API_URL}/api/smsConfig/update/${companyId}` : `${API_URL}/api/smsConfig`;
      const method = existing ? axios.put : axios.post;
      
      await method(endpoint, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      
      toast.success(existing ? "Gateway updated successfully." : "Gateway configured successfully.");
      setExisting(true);
      return true;
    } catch (error) {
      toast.error("Failed to save gateway configuration.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/api/smsConfig/delete/${companyId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      toast.success("Configuration deleted successfully.");
      setExisting(false);
      setShowDeleteModal(false);
      // Reset form
      setProviderName("");
      setUrl("");
      setStaticQueryParams([{ key: "", value: "" }]);
      setCustomHeaders([{ key: "Authorization", value: "" }]);
      setPayloadTemplate("");
    } catch (error) {
      toast.error("Failed to delete configuration.");
    } finally {
      setLoading(false);
    }
  };

  // --- Dynamic Row Handlers ---
  const handleAddRow = (setter, state) => setter([...state, { key: "", value: "" }]);
  const handleRemoveRow = (index, setter, state) => setter(state.filter((_, i) => i !== index));
  const handleRowChange = (index, field, val, setter, state) => {
    const newState = [...state];
    newState[index][field] = val;
    setter(newState);
  };

  // --- Chatbot page context: lets the support chatbot read/fill/save this form ---
  useChatbotPage({
    id: "sms-config",
    title: "SMS Gateway Configuration",
    description: "Connects a third-party SMS provider via GET (query-param mapping) or POST (headers + payload template).",
    apis: SMS_APIS,
    fields: SMS_FIELDS,
    mapSpec: mapSmsSpec,
    getValues: () => ({
      providerName, httpMethod, url, queryParamMapping,
      staticQueryParams: toObject(staticQueryParams),
      contentType, customHeaders: toObject(customHeaders),
      payloadTemplate, responseSuccessField, responseSuccessValue,
    }),
    setValues: (v) => {
      if (v.providerName !== undefined) setProviderName(v.providerName);
      if (v.httpMethod) setHttpMethod(v.httpMethod);
      if (v.url !== undefined) setUrl(v.url);
      if (v.queryParamMapping) setQueryParamMapping({ ...DEFAULT_QUERY_MAP, ...v.queryParamMapping });
      if (v.staticQueryParams) setStaticQueryParams(toRows(v.staticQueryParams, [{ key: "", value: "" }]));
      if (v.contentType !== undefined) setContentType(v.contentType);
      if (v.customHeaders) setCustomHeaders(toRows(v.customHeaders, [{ key: "Authorization", value: "" }]));
      if (v.payloadTemplate !== undefined) setPayloadTemplate(v.payloadTemplate);
      if (v.responseSuccessField !== undefined) setResponseSuccessField(v.responseSuccessField);
      if (v.responseSuccessValue !== undefined) setResponseSuccessValue(v.responseSuccessValue);
    },
    save: handleSaveOrUpdate,
  });

  // --- Animation Variants ---
  const containerVars = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, staggerChildren: 0.1 } }
  };
  const itemVars = {
    hidden: { opacity: 0, x: -10 },
    visible: { opacity: 1, x: 0 }
  };

  return (
    <motion.div 
      initial="hidden" 
      animate="visible" 
      variants={containerVars}
      // Added mt-8 to drop it below the navbar, max-h-[85vh] & overflow-y-auto to contain scrolling
      className="max-w-5xl mx-auto mt-8 mb-4 max-h-[85vh] overflow-y-auto bg-white p-6 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 cfg-font"
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700&family=Inter:wght@400;500;600&display=swap'); .cfg-heading{font-family:'Sora',sans-serif;} .cfg-font{font-family:'Inter',sans-serif;}`}</style>
      <div className="flex justify-between items-center mb-5 border-b pb-3">
        <div>
          <h2 className="text-2xl font-bold cfg-heading text-[#673AB7]">SMS Gateway Configuration</h2>
          <p className="text-sm text-gray-500 mt-1">Connect and map your custom API provider.</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleSaveOrUpdate}
            disabled={loading}
            className="bg-[#673AB7] hover:bg-opacity-90 text-white px-5 py-2 rounded-lg font-medium transition-all shadow-md disabled:opacity-70 text-sm"
          >
            {loading ? "Saving..." : existing ? "Update Config" : "Save Config"}
          </button>
          {existing && (
            <button
              onClick={() => setShowDeleteModal(true)}
              disabled={loading}
              className="bg-[#F46653] hover:bg-opacity-90 text-white px-5 py-2 rounded-lg font-medium transition-all shadow-md text-sm"
            >
              Remove
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <motion.div variants={itemVars}>
          <label className="block text-sm font-semibold text-[#673AB7] mb-1">Provider Name</label>
          <input
            type="text"
            placeholder="e.g., Eurofox, Twilio, MSG91"
            value={providerName}
            onChange={(e) => setProviderName(e.target.value)}
            className="w-full p-2.5 bg-[#EEF2FC] border border-transparent focus:border-[#673AB7] rounded-lg outline-none transition-all text-gray-800 text-sm"
          />
        </motion.div>

        <motion.div variants={itemVars}>
          <label className="block text-sm font-semibold text-[#673AB7] mb-1">HTTP Method</label>
          <div className="flex bg-[#EEF2FC] p-1 rounded-lg">
            {["GET", "POST"].map((method) => (
              <button
                key={method}
                onClick={() => setHttpMethod(method)}
                className={`flex-1 py-1.5 text-sm font-semibold rounded-md transition-all ${
                  httpMethod === method ? "bg-[#673AB7] text-white shadow-md" : "text-gray-500 hover:text-[#673AB7]"
                }`}
              >
                {method} Request
              </button>
            ))}
          </div>
        </motion.div>
      </div>

      <motion.div variants={itemVars} className="mb-5">
        <label className="block text-sm font-semibold text-[#673AB7] mb-1">Base URL</label>
        <input
          type="text"
          placeholder="https://api.provider.com/v1/send"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="w-full p-2.5 bg-[#EEF2FC] border border-transparent focus:border-[#673AB7] rounded-lg outline-none transition-all text-gray-800 text-sm"
        />
      </motion.div>

      {/* --- GET REQUEST CONFIGURATION --- */}
      {httpMethod === "GET" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
            <h3 className="text-md font-bold cfg-heading text-[#673AB7] mb-3 flex items-center">
              <Info className="w-4 h-4 mr-2 text-[#F46653]" /> Query Parameter Mapping
            </h3>
            <p className="text-xs text-gray-500 mb-3">Map our system variables to your provider's expected URL parameters.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.keys(queryParamMapping).map((key) => (
                <div key={key} className="flex items-center space-x-2">
                  <span className="w-24 text-xs font-medium text-gray-700 bg-gray-200 py-2 px-2 rounded-md text-center">
                    {key}
                  </span>
                  <span className="text-gray-400 font-bold">=</span>
                  <input
                    type="text"
                    value={queryParamMapping[key]}
                    onChange={(e) => setQueryParamMapping({ ...queryParamMapping, [key]: e.target.value })}
                    placeholder="Parameter name"
                    className="flex-1 p-2 text-sm bg-white border border-gray-300 rounded-lg outline-none focus:border-[#673AB7]"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-md font-bold cfg-heading text-[#673AB7]">Static Query Params (API Keys)</h3>
              <button onClick={() => handleAddRow(setStaticQueryParams, staticQueryParams)} className="text-sm flex items-center text-[#673AB7] font-semibold">
                <Plus className="w-4 h-4 mr-1" /> Add Param
              </button>
            </div>
            <div className="space-y-2">
              {staticQueryParams.map((item, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="text"
                    placeholder="Key (e.g., apiKey)"
                    value={item.key}
                    onChange={(e) => handleRowChange(index, "key", e.target.value, setStaticQueryParams, staticQueryParams)}
                    className="flex-1 p-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-[#673AB7]"
                  />
                  <input
                    type="text"
                    placeholder="Value"
                    value={item.value}
                    onChange={(e) => handleRowChange(index, "value", e.target.value, setStaticQueryParams, staticQueryParams)}
                    className="flex-1 p-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-[#673AB7]"
                  />
                  <button onClick={() => handleRemoveRow(index, setStaticQueryParams, staticQueryParams)} className="text-red-500">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* --- POST REQUEST CONFIGURATION --- */}
      {httpMethod === "POST" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
             <div className="flex justify-between items-center mb-3">
              <h3 className="text-md font-bold cfg-heading text-[#673AB7]">Custom Headers</h3>
              <button onClick={() => handleAddRow(setCustomHeaders, customHeaders)} className="text-sm flex items-center text-[#673AB7] font-semibold">
                <Plus className="w-4 h-4 mr-1" /> Add Header
              </button>
            </div>
            <div className="space-y-2">
              {customHeaders.map((item, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="text"
                    placeholder="Header Name"
                    value={item.key}
                    onChange={(e) => handleRowChange(index, "key", e.target.value, setCustomHeaders, customHeaders)}
                    className="flex-1 p-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-[#673AB7]"
                  />
                  <input
                    type="text"
                    placeholder="Value (e.g., Bearer Token)"
                    value={item.value}
                    onChange={(e) => handleRowChange(index, "value", e.target.value, setCustomHeaders, customHeaders)}
                    className="flex-1 p-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-[#673AB7]"
                  />
                  <button onClick={() => handleRemoveRow(index, setCustomHeaders, customHeaders)} className="text-red-500">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#F3E8FF] p-4 rounded-xl border border-[#E9D5FF]">
            <h3 className="text-md font-bold cfg-heading text-[#673AB7] mb-1">Payload Template</h3>
            <p className="text-xs text-gray-600 mb-3">
              You must use these exact variables in your JSON: <code className="bg-white px-1 py-0.5 rounded text-[#F46653]">{"{{phone}}"}</code>, <code className="bg-white px-1 py-0.5 rounded text-[#F46653]">{"{{message}}"}</code>, <code className="bg-white px-1 py-0.5 rounded text-[#F46653]">{"{{senderId}}"}</code>, <code className="bg-white px-1 py-0.5 rounded text-[#F46653]">{"{{dltId}}"}</code>.
            </p>
            <textarea
              rows="4"
              value={payloadTemplate}
              onChange={(e) => setPayloadTemplate(e.target.value)}
              placeholder='{&#10;  "destination": "{{phone}}",&#10;  "body": "{{message}}",&#10;  "sender": "{{senderId}}",&#10;  "dlt_id": "{{dltId}}"&#10;}'
              className="w-full p-3 font-mono text-xs border border-transparent rounded-lg outline-none focus:border-[#673AB7]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-xl border border-gray-100">
            <div>
              <label className="block text-xs font-semibold text-[#673AB7] mb-1">Success Response Field</label>
              <input
                type="text"
                placeholder="e.g., status"
                value={responseSuccessField}
                onChange={(e) => setResponseSuccessField(e.target.value)}
                className="w-full p-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-[#673AB7]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#673AB7] mb-1">Success Expected Value</label>
              <input
                type="text"
                placeholder="e.g., success"
                value={responseSuccessValue}
                onChange={(e) => setResponseSuccessValue(e.target.value)}
                className="w-full p-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-[#673AB7]"
              />
            </div>
          </div>
        </motion.div>
      )}

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-sm"
          >
            <motion.div 
              initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="bg-white p-6 rounded-2xl shadow-2xl max-w-md w-full"
            >
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 text-[#F46653] mx-auto mb-3">
                <Trash2 className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold cfg-heading text-center text-gray-900 mb-2">Confirm Gateway Deletion</h3>
              <p className="text-center text-sm text-gray-500 mb-5">Are you sure you want to completely remove this configuration? It will disrupt pending campaigns.</p>
              
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-5 py-2 text-sm text-gray-700 font-semibold bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-5 py-2 text-sm bg-[#F46653] text-white font-semibold rounded-lg hover:bg-opacity-90 transition-colors"
                >
                  Delete Gateway
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-5 text-center text-xs text-gray-400">
        Need assistance with configuration? Contact info@deetyasoft.com
      </div>
    </motion.div>
  );
};

export default SmsConfigManager;